import React from 'react';

interface StatsProps {
  stats: {
    correct: number;
    incorrect: number;
    total: number;
    streak: number;
    maxStreak: number;
  };
  darkMode?: boolean;
}

const Stats: React.FC<StatsProps> = ({ stats, darkMode = false }) => {
  const accuracy = stats.total > 0 ? Math.round((stats.correct / stats.total) * 100) : 0;

  return (
    <div className="mb-2">
      {/* Header */}
      <div className="text-center mb-2">
        <h3 className={`font-semibold text-xs tracking-wider uppercase ${
          darkMode ? 'text-gray-300' : 'text-gray-600'
        }`}>Study Progress</h3>
      </div>
      
      {/* Stats Box */}
      <div className={`relative overflow-hidden rounded-xl p-5 ${
        darkMode 
          ? 'bg-gray-900/80 backdrop-blur-xl border border-gray-800/50 shadow-lg' 
          : 'bg-white/90 backdrop-blur-xl border border-gray-200/50 shadow-md'
      }`}>
        {/* Subtle gradient overlay */}
        <div className={`absolute inset-0 bg-gradient-to-br ${
          darkMode 
            ? 'from-gray-800/20 to-gray-900/20' 
            : 'from-gray-50/50 to-white/50'
        }`}></div>
        
        {/* Content */}
        <div className="relative z-10">
          {/* Stats Grid */}
          <div className="grid grid-cols-4 gap-0">
            {/* Correct */}
            <div className="text-center group relative">
              <div className={`text-xl font-bold mb-1 transition-all duration-300 ${
                darkMode ? 'text-gray-100' : 'text-gray-700'
              } group-hover:scale-110`}>
                {stats.correct}
              </div>
              <div className={`text-xs font-medium tracking-wider uppercase opacity-80 ${
                darkMode ? 'text-gray-400' : 'text-gray-500'
              }`}>Correct</div>
              <div className={`w-6 h-0.5 mx-auto mt-1 rounded-full ${
                darkMode ? 'bg-gray-400/30' : 'bg-gray-500/30'
              }`}></div>
              {/* Right Divider */}
              <div className={`absolute right-0 top-1/2 transform -translate-y-1/2 w-px h-8 ${
                darkMode 
                  ? 'bg-gradient-to-b from-gray-700/50 via-gray-600/30 to-gray-700/50 shadow-sm' 
                  : 'bg-gradient-to-b from-gray-300/50 via-gray-400/30 to-gray-300/50 shadow-sm'
              }`}></div>
            </div>

            {/* Incorrect */}
            <div className="text-center group relative">
              <div className={`text-xl font-bold mb-1 transition-all duration-300 ${
                darkMode ? 'text-gray-100' : 'text-gray-700'
              } group-hover:scale-110`}>
                {stats.incorrect}
              </div>
              <div className={`text-xs font-medium tracking-wider uppercase opacity-80 ${
                darkMode ? 'text-gray-400' : 'text-gray-500'
              }`}>Review</div>
              <div className={`w-6 h-0.5 mx-auto mt-1 rounded-full ${
                darkMode ? 'bg-gray-400/30' : 'bg-gray-500/30'
              }`}></div>
              {/* Right Divider */}
              <div className={`absolute right-0 top-1/2 transform -translate-y-1/2 w-px h-8 ${
                darkMode 
                  ? 'bg-gradient-to-b from-gray-700/50 via-gray-600/30 to-gray-700/50 shadow-sm' 
                  : 'bg-gradient-to-b from-gray-300/50 via-gray-400/30 to-gray-300/50 shadow-sm'
              }`}></div>
            </div>

            {/* Accuracy */}
            <div className="text-center group relative">
              <div className={`text-xl font-bold mb-1 transition-all duration-300 ${
                darkMode ? 'text-gray-100' : 'text-gray-700'
              } group-hover:scale-110`}>
                {accuracy}%
              </div>
              <div className={`text-xs font-medium tracking-wider uppercase opacity-80 ${
                darkMode ? 'text-gray-400' : 'text-gray-500'
              }`}>Accuracy</div>
              <div className={`w-6 h-0.5 mx-auto mt-1 rounded-full ${
                darkMode ? 'bg-gray-400/30' : 'bg-gray-500/30'
              }`}></div>
              {/* Right Divider */}
              <div className={`absolute right-0 top-1/2 transform -translate-y-1/2 w-px h-8 ${
                darkMode 
                  ? 'bg-gradient-to-b from-gray-700/50 via-gray-600/30 to-gray-700/50 shadow-sm' 
                  : 'bg-gradient-to-b from-gray-300/50 via-gray-400/30 to-gray-300/50 shadow-sm'
              }`}></div>
            </div>

            {/* Streak */}
            <div className="text-center group relative">
              <div className={`text-xl font-bold mb-1 transition-all duration-300 ${
                darkMode ? 'text-gray-100' : 'text-gray-700'
              } group-hover:scale-110`}>
                {stats.streak}
              </div>
              <div className={`text-xs font-medium tracking-wider uppercase opacity-80 ${
                darkMode ? 'text-gray-400' : 'text-gray-500'
              }`}>Streak</div>
              <div className={`w-6 h-0.5 mx-auto mt-1 rounded-full ${
                darkMode ? 'bg-gray-400/30' : 'bg-gray-500/30'
              }`}></div>
            </div>
          </div>
        </div>
      </div>
    </div>
  );
};

export default Stats;
